import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CheckCircle2, Clock, Link2, Mail, Phone, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import { badgeBase, statusClasses } from "@/lib/colors";
import { cn } from "@/lib/utils";
import {
	chatSearch,
	formatDateTime,
	helpdeskApi,
	type PairingItem,
	type PharmacyName,
	SUGGESTION_REASON_LABEL,
	useHelpdeskKey,
	useHelpdeskMutation,
} from "./api";
import { ContactLabelButton } from "./contact-label";
import { OfficeDeviceButton } from "./office-device";
import { PharmacySearch } from "./pharmacy-search";

/**
 * Jeden adres do sparowania. Podpowiedzi tylko podpowiadają — apteki wybiera
 * człowiek (jedną lub kilka; przy kilku apteka wybierana jest przy zgłoszeniu).
 */
export function PairingCard({
	item,
	showConversationLink = true,
}: {
	item: PairingItem;
	showConversationLink?: boolean;
}) {
	const [selected, setSelected] = useState<PharmacyName[]>([]);
	const pair = useHelpdeskMutation(
		() =>
			helpdeskApi.pair(
				item.email,
				selected.map((p) => p.idfirm),
			),
		{
			success: () =>
				selected.length === 1
					? `${item.email} → ${selected[0].name}`
					: `${item.email} sparowany z ${selected.length} aptekami — aptekę wybierzesz przy zgłoszeniu`,
			error: "Nie udało się sparować adresu",
		},
	);
	const toggle = (p: PharmacyName) =>
		setSelected((prev) =>
			prev.some((x) => x.idfirm === p.idfirm)
				? prev.filter((x) => x.idfirm !== p.idfirm)
				: [...prev, p],
		);

	return (
		// @container: ta sama karta w liście parowania i w wąskim panelu czatu.
		<li
			className="@container space-y-2 rounded-md border bg-card p-3"
			data-pairing-email={item.email}
		>
			<div className="flex flex-wrap items-start gap-x-3 gap-y-1">
				<Mail className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
				<div className="min-w-0 flex-1">
					<p className="truncate text-sm font-medium">
						{item.email}
						{item.name ? (
							<span className="ml-1.5 font-normal text-muted-foreground">{item.name}</span>
						) : null}
					</p>
					<p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
						<span>
							{item.ticketCount} zgłoszeń{item.openCount ? ` · ${item.openCount} otwartych` : ""}
						</span>
						<span className="inline-flex items-center gap-1">
							<Clock className="size-3" aria-hidden />
							{formatDateTime(item.lastAt)}
						</span>
						{item.phone ? (
							<a
								href={`tel:${item.phone.replace(/\s/g, "")}`}
								className="inline-flex items-center gap-1 text-primary hover:underline"
							>
								<Phone className="size-3" aria-hidden />
								{item.phone}
							</a>
						) : null}
						{showConversationLink ? (
							<Link
								to="/helpdesk/czat"
								search={chatSearch({ email: item.email })}
								className="text-primary hover:underline"
							>
								pokaż rozmowę
							</Link>
						) : null}
					</p>
					{item.sampleSubjects.length ? (
						<ul className="mt-1 space-y-0.5 text-xs">
							{item.sampleSubjects.slice(0, 3).map((s) => (
								<li key={s} className="truncate">
									„{s}”
								</li>
							))}
						</ul>
					) : null}
				</div>
			</div>

			{item.suggestions.length ? (
				<div className="flex flex-wrap items-center gap-1.5 text-xs">
					<span className="text-muted-foreground">Podpowiedzi:</span>
					{item.suggestions.map((s) => {
						const on = selected.some((p) => p.idfirm === s.idfirm);
						return (
							<button
								key={`${s.idfirm}-${s.reason}`}
								type="button"
								aria-pressed={on}
								title={s.detail ?? undefined}
								onClick={() => toggle({ idfirm: s.idfirm, name: s.name })}
								className={cn(
									"inline-flex items-center gap-1 rounded-md border px-2 py-0.5",
									on ? "border-primary bg-primary/10" : "border-border hover:bg-muted",
								)}
							>
								{s.name} <span className="font-mono text-muted-foreground">{s.idfirm}</span>
								<span className={cn(badgeBase, "px-1 py-0", statusClasses.info)}>
									{SUGGESTION_REASON_LABEL[s.reason] ?? s.reason}
								</span>
							</button>
						);
					})}
				</div>
			) : null}

			<div className="grid gap-2 @xl:grid-cols-[1fr_auto]">
				<div className="space-y-1.5">
					<PharmacySearch
						onPick={(p) => !selected.some((x) => x.idfirm === p.idfirm) && toggle(p)}
						exclude={selected.map((p) => p.idfirm)}
					/>
					{selected.length ? (
						<div className="flex flex-wrap gap-1.5 text-xs">
							{selected.map((p) => (
								<span
									key={p.idfirm}
									className="inline-flex items-center gap-1 rounded-md border border-primary bg-primary/10 px-2 py-0.5"
								>
									{p.name} <span className="font-mono">{p.idfirm}</span>
									<button type="button" aria-label={`Usuń ${p.name}`} onClick={() => toggle(p)}>
										<X className="size-3" />
									</button>
								</span>
							))}
						</div>
					) : null}
				</div>
				<div className="flex items-start gap-2">
					<Button
						size="sm"
						disabled={!selected.length || pair.isPending}
						onClick={() => pair.mutate(undefined)}
					>
						<CheckCircle2 className="mr-1.5 size-4" aria-hidden />
						Sparuj{selected.length > 1 ? ` (${selected.length})` : ""}
					</Button>
					<ContactLabelButton email={item.email} label={null} />
					<OfficeDeviceButton email={item.email} deviceUuid={null} />
				</div>
			</div>
		</li>
	);
}

/** Kolejka parowania: adresy bez apteki. Pusta kolejka = krótka informacja. */
export function PairingQueue() {
	const key = useHelpdeskKey();
	const { data, isLoading, isError } = useQuery({
		queryKey: key("pairing"),
		queryFn: helpdeskApi.pairing,
		staleTime: 30_000,
	});
	const items = (data?.items ?? []).filter((i) => i.pairedIdfirms.length === 0);
	if (isLoading) return <p className="text-xs text-muted-foreground">Wczytywanie…</p>;
	if (isError)
		return (
			<p className="text-xs text-status-error-text">Nie udało się pobrać kolejki parowania.</p>
		);
	return (
		<section className="space-y-3 rounded-lg border border-status-warning-border bg-status-warning-bg/30 p-4 max-md:p-3">
			<div className="flex items-center gap-2">
				<Link2 className="size-4 text-status-warning-text" aria-hidden />
				<h3 className="text-sm font-semibold">
					Do sparowania{" "}
					<span className="ml-1 text-xs font-normal text-muted-foreground">({items.length})</span>
				</h3>
			</div>
			<p className="text-xs text-muted-foreground">
				Maile z adresów bez apteki czekają tutaj. Nic nie paruje się samo — wybierz aptekę (albo
				kilka) albo oznacz adres jako spoza aptek, np. „Biuro”. Sparowane adresy, etykiety i
				odpięcia są w zakładce Kontakty.
			</p>
			{items.length ? (
				<ul className="space-y-2">
					{items.map((item) => (
						<PairingCard key={item.email} item={item} />
					))}
				</ul>
			) : (
				<p className="text-xs text-muted-foreground">Wszystkie adresy są sparowane.</p>
			)}
		</section>
	);
}

/** Odpięcie adresu od apteki (zgłoszenia zostają przy aptece). */
export function UnpairButton({ email, pharmacy }: { email: string; pharmacy: PharmacyName }) {
	const [open, setOpen] = useState(false);
	const unpair = useHelpdeskMutation(() => helpdeskApi.unpair(email, pharmacy.idfirm), {
		success: "Adres odpięty",
		error: "Nie udało się odpiąć adresu",
	});
	return (
		<>
			<button
				type="button"
				aria-label={`Odepnij ${email} od ${pharmacy.name}`}
				className="rounded-sm p-0.5 hover:bg-muted"
				onClick={() => setOpen(true)}
			>
				<X className="size-3" />
			</button>
			<ConfirmDialog
				open={open}
				onOpenChange={setOpen}
				title="Odpiąć adres od apteki?"
				description={`${email} przestanie być przypisany do ${pharmacy.name}. Dotychczasowe zgłoszenia zostają przy aptece.`}
				variant="warning"
				confirmLabel="Odepnij"
				isLoading={unpair.isPending}
				onConfirm={() => unpair.mutate(undefined, { onSuccess: () => setOpen(false) })}
			/>
		</>
	);
}
